import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import TaskFormModal from "../Task/TaskFormModal";
import ActivityFormModal from "../ActivityFormModal";
import { Modal } from "react-bootstrap";
import RatingForm from "../Rating/RatingForm";
import AskFilledNote from "./AskFilledNote";
import { createRating } from "../../services/ratingService";
import { OrganizationRating, RatingContextType, RatingType } from "../../types/Rating";
import LeaveRequestDrawer from "../../pages/people/LeaveRequestDrawer";
import { getReq, postReq, setAskDraftOrigin } from "../../utils/request";
import Button from "../Button";
import { OPEN_DRAFT_EVENT } from "../../utils/assistant";
import { AiDraft } from "../../types/Ai";
import { MyLeave } from "../../types/Hr";
import { EntityParentType, Task } from "../../types/Task";
import { handOverDraft } from "../../utils/draftHandoff";
import { reportAskAction } from "../../utils/askReturn";

/** Which form fields a draft filled, in words, for the note at the top of the form. */
const FILLED_LABELS: Record<string, Record<string, string>> = {
    task: { name: "name", description: "notes", priority: "priority", endAt: "due date", assignedUser: "who it's for", parentId: "linked record" },
    activity: { activityType: "type", subject: "subject", description: "notes", outcome: "outcome", activityDate: "date", nextFollowUpDate: "follow-up" },
    rating: { overallRating: "every score", notes: "notes", contextId: "mission" },
    leave: { leaveTypeId: "leave type", fromDate: "first day", toDate: "last day", reason: "reason" },
};

export const filledFields = (draft: AiDraft): string[] => {
    const labels = FILLED_LABELS[draft.kind] ?? {};
    return Object.keys(labels).filter((key) => {
        const value = (draft.values ?? {})[key];
        return value !== null && value !== undefined && value !== "";
    }).map((key) => labels[key]);
};

/**
 * Opens a form Ask prepared (D9): the real form, filled in, for the person to
 * check and save. Nothing is saved here — saving goes through the form's own
 * request and the server's normal checks, as the person. Mounted once.
 */
const DraftHost: React.FC = () => {
    const [draft, setDraft] = useState<AiDraft | null>(null);
    const [leave, setLeave] = useState<MyLeave | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const handleOpen = (event: Event) => {
            const next = (event as CustomEvent<AiDraft>).detail;
            if (!next?.kind) return;
            if (next.kind === "leave") {
                getReq<MyLeave>("hr/leave/mine")
                    .then((response) => {
                        if (response?.status === 200 && response.body?.employmentId) {
                            setLeave(response.body);
                            setDraft(next);
                        } else {
                            toast.error("Leave requests need a current employment record.");
                        }
                    })
                    .catch(() => toast.error("Couldn't open the leave form."));
                return;
            }
            // Movements: open the mission's movements with them added.
            if (next.kind === "movements") {
                const missionId = Number(next.values?.missionId);
                if (!missionId) return;
                handOverDraft("movements", missionId, next.values ?? {});
                navigate(`/mission/${missionId}?tab=itineraries`);
                return;
            }
            // Converting a request: open it with its own Convert step ready.
            if (next.kind === "convert_request") {
                const requestId = Number(next.values?.missionRequestId);
                if (requestId) navigate(`/mission-request/${requestId}?action=convert`);
                return;
            }
            // A whole new mission: open the create page with it filled in.
            if (next.kind === "mission") {
                handOverDraft("mission", 0, next.values ?? {});
                navigate("/mission");
                return;
            }
            // A new mission request: open the create page with it filled in.
            if (next.kind === "request") {
                handOverDraft("request", 0, next.values ?? {});
                navigate("/mission-request");
                return;
            }
            // Lead changes: open the lead in edit mode with them applied.
            if (next.kind === "lead_edit") {
                const leadId = Number(next.values?.leadId);
                if (!leadId) return;
                handOverDraft("lead_edit", leadId, next.values ?? {});
                navigate(`/lead/${leadId}?mode=edit`);
                return;
            }
            // Changes to an existing request: open it with each change shown before → after.
            if (next.kind === "request_edit") {
                const requestId = Number(next.values?.missionRequestId);
                if (!requestId) return;
                handOverDraft("request_edit", requestId, next.values ?? {});
                navigate(`/mission-request/${requestId}`);
                return;
            }
            // New dates for an existing mission: set on the mission, unsaved, with a note.
            if (next.kind === "mission_dates") {
                const missionId = Number(next.values?.missionId);
                if (!missionId) return;
                handOverDraft("mission_dates", missionId, { dateFrom: next.values?.dateFrom, dateTo: next.values?.dateTo });
                navigate(`/mission/${missionId}?tab=information`);
                return;
            }
            // A movement's new time, or a vehicle for its team: open its itinerary with it applied.
            if (next.kind === "movement_edit" || next.kind === "vehicle") {
                const itineraryId = Number(next.values?.itineraryId);
                if (!itineraryId || !next.values?.missionId) return;
                handOverDraft(next.kind, itineraryId, next.values ?? {});
                navigate(`/mission/${next.values.missionId}?tab=itineraries&itineraryId=${itineraryId}${next.kind === "vehicle" ? "&itinDrawer=teams" : ""}`);
                return;
            }
            // New details for a client, provider or contact: the record opens with Fill with AI reading them — each change current → new to tick.
            if (next.kind === "org_edit" || next.kind === "contact_edit") {
                const id = Number(next.kind === "org_edit" ? next.values?.organizationId : next.values?.userDetailId);
                if (!id || !next.values?.text) return;
                handOverDraft("record_paste", id, { text: String(next.values.text) });
                navigate(next.kind === "contact_edit" ? `/contact/${id}` : `/${next.values?.kind === "provider" ? "provider" : "client"}/${id}`);
                return;
            }
            // A driver joins the movement's itinerary team: open its Teams with them added.
            if (next.kind === "driver") {
                const itineraryId = Number(next.values?.itineraryId);
                if (!itineraryId || !next.values?.missionId) return;
                handOverDraft("driver", itineraryId, next.values ?? {});
                navigate(`/mission/${next.values.missionId}?tab=itineraries&itineraryId=${itineraryId}&itinDrawer=teams`);
                return;
            }
            // An incident lives on its movement: open that movement's Incidents drawer with it.
            if (next.kind === "incident") {
                const legId = Number(next.values?.legId);
                if (!legId || !next.values?.missionId) return;
                handOverDraft("incident", legId, next.values ?? {});
                navigate(`/mission/${next.values.missionId}?tab=itineraries&itineraryId=${next.values.itineraryId ?? ""}&legId=${legId}&legDrawer=incidents`);
                return;
            }
            setDraft(next);
        };
        window.addEventListener(OPEN_DRAFT_EVENT, handleOpen);
        return () => window.removeEventListener(OPEN_DRAFT_EVENT, handleOpen);
    }, [navigate]);

    // saves made while the filled form is open show "via Ask" in the record's history
    useEffect(() => {
        setAskDraftOrigin(!!draft && !draft.manual);
        return () => setAskDraftOrigin(false);
    }, [draft]);

    // the forms reset when these change identity, so build them once per draft
    const values = useMemo(() => (draft?.values ?? {}) as Record<string, any>, [draft]);
    const taskData = useMemo<Partial<Task>>(() => ({
        name: values.name ?? null,
        description: values.description ?? null,
        priority: values.priority ?? null,
        status: values.status ?? null,
        endAt: values.endAt ?? null,
        assignedUser: values.assignedUser ?? null,
    }), [values]);
    const askFilled = useMemo(() => (draft && !draft.manual ? filledFields(draft) : []), [draft]);
    const ratingInitial = useMemo(() => ({ notes: values.notes ?? null }), [values]);
    const activityData = useMemo(() => ({
        activityType: values.activityType,
        direction: values.direction,
        subject: values.subject ?? "",
        description: values.description ?? null,
        outcome: values.outcome ?? null,
        activityDate: values.activityDate ? new Date(values.activityDate).toISOString() : new Date().toISOString(),
        nextFollowUpDate: values.nextFollowUpDate ?? null,
        nextFollowUpNotes: values.nextFollowUpNotes ?? null,
    }), [values]);

    // closed without saving (after a save the action is already "saved", so this does nothing)
    const close = () => { reportAskAction("closed"); setDraft(null); };
    const saved = () => reportAskAction("saved");
    if (!draft) return null;

    if (draft.kind === "task") {
        return (
            <TaskFormModal
                show
                onClose={close}
                onSaved={() => { toast.success("Task saved"); saved(); close(); }}
                parentType={(values.parentType as EntityParentType) ?? null}
                parentId={values.parentId ?? null}
                initialData={taskData}
                askFilled={askFilled}
            />
        );
    }

    if (draft.kind === "activity" && values.parentType && values.parentId) {
        return (
            <ActivityFormModal
                show
                onClose={close}
                onSaved={() => { toast.success("Activity saved"); saved(); close(); }}
                parentType={String(values.parentType)}
                parentId={Number(values.parentId)}
                initial={activityData as any}
                askFilled={askFilled}
            />
        );
    }

    if (draft.kind === "copy_mission" && values.sourceMissionId) {
        const copy = async () => {
            setIsSubmitting(true);
            const response = await postReq<{ missionId: number }>(`missions/${values.sourceMissionId}/duplicate`, {}).catch(() => null);
            setIsSubmitting(false);
            const newId = response?.body?.missionId;
            if ((response?.status === 201 || response?.status === 200) && newId) {
                if (values.dateFrom) handOverDraft("mission_dates", newId, { dateFrom: values.dateFrom, dateTo: values.dateTo ?? values.dateFrom });
                toast.success(`Copied ${values.sourceReference} as a new mission${values.dateFrom ? " — check the dates, then save" : ""}.`);
                saved();
                close();
                navigate(`/mission/${newId}?tab=information`);
            } else {
                toast.error("Couldn't copy the mission");
            }
        };
        return (
            <Modal show onHide={close} centered>
                <Modal.Header closeButton><Modal.Title>Copy {values.sourceReference} as a new mission</Modal.Title></Modal.Header>
                <Modal.Body>
                    <p>The new mission gets the clients, principals, places, services with their pricing, and the itineraries with their teams.</p>
                    <p>It does <strong>not</strong> get movements, dates, documents, risk assessments or finance — you plan those on the new mission.</p>
                    {values.dateFrom && <AskFilledNote fields={[`dates (${values.dateFrom}${values.dateTo && values.dateTo !== values.dateFrom ? ` → ${values.dateTo}` : ""}) — set on the new mission for you to save`]} />}
                </Modal.Body>
                <Modal.Footer>
                    <Button type="button" variant="ghost" onClick={close} title="Don't copy">Cancel</Button>
                    <Button type="button" variant="primary" icon="files" loading={isSubmitting} loadingText="Copying…" onClick={copy}
                        title="Create the new mission now, as you (Missions create needed)">
                        Copy as new mission
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    if (draft.kind === "rating" && values.organizationId) {
        const submit = async (rating: Partial<OrganizationRating>) => {
            setIsSubmitting(true);
            const response = await createRating(rating).catch(() => null);
            setIsSubmitting(false);
            if (response && (response.status === 200 || response.status === 201)) {
                toast.success("Rating saved");
                saved();
                close();
            } else {
                toast.error("Couldn't save the rating");
            }
        };
        return (
            <Modal show onHide={close} size="lg" centered>
                <Modal.Header closeButton><Modal.Title>Rate {values.organizationName}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <AskFilledNote fields={askFilled} />
                    <RatingForm
                        organizationId={Number(values.organizationId)}
                        organizationName={values.organizationName}
                        ratingType={values.ratingType === "client" ? RatingType.CLIENT : RatingType.PROVIDER}
                        contextType={values.contextType === "mission" ? RatingContextType.MISSION : RatingContextType.STANDALONE}
                        contextId={values.contextId ?? null}
                        initialRating={ratingInitial}
                        defaultScore={Number(values.overallRating) || undefined}
                        onSubmit={submit}
                        onCancel={close}
                        isSubmitting={isSubmitting}
                    />
                </Modal.Body>
            </Modal>
        );
    }

    if (draft.kind === "leave" && leave) {
        return (
            <LeaveRequestDrawer
                open
                types={leave.types}
                balances={leave.balances}
                initial={values}
                askFilled={askFilled}
                onClose={close}
                onSaved={() => { toast.success("Leave request sent"); saved(); close(); }}
            />
        );
    }
    return null;
};

export default DraftHost;
